import { LoanOfferMode, LoanOfferState } from '@shared/sec-loans'
import { bffDateStringToFormDateString, dateToFormDateString } from '@ui-common/utils'
import { Card } from '@ui-components/containers/Card'
import { AssistantStep } from '@ui-components/layout/AssistantStep'
import { useIntl } from 'react-intl'

import { useLoanOfferAnalyticsService, useLoanOfferPageViewEvent } from '../../analytics/analyticsContext'
import { getLoanOfferLocalizedEnums } from '../../utils/getLoanOfferLocalizedEnums'
import { resolveLoanOfferLabels } from '../../utils/helpers'
import { WizardStep } from '../../wizard/wizardMachine'
import { useExistingLoanOfferContext } from '../LoanOfferContext'

export const StatusStep = ({
  onGoToApplicationsClick,
  onNewApplicationClick,
}: {
  onGoToApplicationsClick: () => void
  onNewApplicationClick: () => void
}) => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { step, proceed, loanOffer, exit } = useExistingLoanOfferContext()

  const { analyticsService } = useLoanOfferAnalyticsService()
  useLoanOfferPageViewEvent(step)

  const { loanOfferModeDescriptionTexts } = getLoanOfferLocalizedEnums(formatMessage)
  const { loanOfferMode } = loanOffer
  const isAutomaticOffer = loanOfferMode === LoanOfferMode.Automatic

  const { title: subHeaderTitle, description: subHeaderDescription } = resolveLoanOfferLabels({
    offeredLoansAmount: loanOffer?.offeredLoans.length,
    firstLoanOfferType: loanOffer.offeredLoans[0]?.loanType,
    isFirstOfferLoanTransfer: loanOffer.offeredLoans[0]?.isLoanTransfer,
    formatMessage,
  })

  const cardDescription = isAutomaticOffer
    ? null
    : formatMessage(
        { id: 'loan-offer-status-step-manual-offer-description' },
        { date: dateToFormDateString(new Date(loanOffer.updated * 1_000)) },
      )

  const getprimaryHeadingAdditionalText = (loanOfferState: LoanOfferState) => {
    switch (loanOfferState) {
      case LoanOfferState.OfferActive:
        return formatMessage(
          { id: 'loan-offer-status-step-additional-info-offer-active' },
          {
            pricingDate: bffDateStringToFormDateString(loanOffer.expiration.pricingValidUntilDate),
            termsDate: bffDateStringToFormDateString(loanOffer.expiration.termsValidUntilDate),
          },
        )
      case LoanOfferState.PricingExpired:
        return formatMessage(
          { id: 'loan-offer-status-step-additional-info-pricing-expired' },
          { termsDate: bffDateStringToFormDateString(loanOffer.expiration.termsValidUntilDate) },
        )
      case LoanOfferState.TermsExpired:
        return formatMessage({ id: 'loan-offer-status-step-additional-info-terms-expired' })
      default:
        const exhaustiveCheck: never = loanOfferState
        throw new Error(`Unsupported loanOfferState: ${exhaustiveCheck}`)
    }
  }

  return (
    <AssistantStep
      stepId={WizardStep.StatusStep}
      emoji={'raisedHands'}
      topic={formatMessage({ id: 'loan-offer-status-step-heading' })}
      primaryHeadingLeadText={formatMessage({ id: 'loan-offer-status-step-lead-text' })}
      primaryHeadingAdditionalText={getprimaryHeadingAdditionalText(loanOffer.state)}
      shouldUsePaperContainer={false}
      headerButtonProps={{
        headerButtonLabel: formatMessage({ id: 'common-exit' }),
        onHeaderButtonClick: exit,
      }}
      subHeaderOptions={{
        title: subHeaderTitle,
        description: subHeaderDescription,
        isFixedSubHeader: true,
        secondaryTopBarMenuProps: {
          menuAriaLabelOpen: intl.formatMessage({ id: 'common-open-additional-features' }),
          menuAriaLabelClose: intl.formatMessage({ id: 'common-close-additional-features' }),
          menuOptions: [
            {
              text: formatMessage({ id: 'loan-offer-status-step-menu-new-application-label' }),
              onClick: onNewApplicationClick,
            },
            {
              text: formatMessage({ id: 'loan-offer-status-step-menu-own-applications-label' }),
              onClick: onGoToApplicationsClick,
            },
          ],
        },
      }}
    >
      <Card
        variant="primary"
        data-testid="offer-card"
        title={{
          text: loanOfferModeDescriptionTexts[loanOffer.loanOfferMode],
          $tagName: 'h2',
          $variant: 'heading',
        }}
        description={cardDescription}
        icon="paymentSlipIcon"
        clickOptions={{
          onClick: () => {
            analyticsService.sendWizardStepCustomEvent(step, 'ContinueButton')
            proceed()
          },
        }}
      />
    </AssistantStep>
  )
}
